import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchActivities,
  fetchActivity,
  updateActivity,
  createActivity,
  deleteActivity,
} from "../api/activity";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteActivityAsync = createAsyncThunk(
  "activities/deleteActivityAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteActivity(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "delete_activity");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_activity");
    }
  }
);

export const createActivityAsync = createAsyncThunk(
  "activities/createActivityAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createActivity(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_activity");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_activity");
    }
  }
);

export const updateActivityAsync = createAsyncThunk(
  "activities/updateActivityAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateActivity(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_activity");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_activity");
    }
  }
);

export const getActivitiesAsync = createAsyncThunk(
  "activities/getActivitiesAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchActivities(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_activities");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_activities");
    }
  }
);

export const getActivityAsync = createAsyncThunk(
  "activities/getActivityAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchActivity(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_activity");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_activity");
    }
  }
);

const activitySlice = createSlice({
  name: "activities",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getActivitiesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getActivitiesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestActivitysCount = action.payload.newCount;
      })
      .addCase(getActivitiesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente aktivitetene.");
      })
      .addCase(updateActivityAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateActivityAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateActivityAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere aktiviteten.");
      })
      .addCase(createActivityAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createActivityAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createActivityAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette aktiviteten.");
      })
      .addCase(deleteActivityAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteActivityAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteActivityAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette aktiviteten.");
      })
      .addCase(getActivityAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getActivityAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.activity = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getActivityAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente aktiviteten.");
      });
  },
});

export const { reducer } = activitySlice;

export default activitySlice;
